import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DrawOutlined as DrawOutlinedIcon } from '@mui/icons-material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import "./styles.css";
import SignatureModal from './SignatureModal';
import SignatureReviewModal from './SignatureReviewModal';
import AcknowledgementConfigModal from './AcknowledgementConfigModal';
import AcknowledgementSignModal from './AcknowledgementSignModal';
import { fontFamilyForKey } from './AdoptSignatureModal';
import LoadSpinner from '../../../utilities/LoadSpinner';
import { getRightworkAPI, postRightworkAPI } from '../requests/utilities/requests';
import {
  API_get_file_upload,
  API_post_learning_signature_completion,
  API_get_learning_signature_completion
} from '../../../constants';

export const Signature = (props) => {
  const { block, editor } = props;
  const { pdfName, file_uuid, totalPages, saveToProfile, title, acknowledgementText } = block.props;

  // Mode: "acknowledgement" (default, no PDF) | "pdf" (legacy markup). Fall back
  // to pdf for legacy blocks that have a file but no explicit mode.
  const signatureMode = block.props.signatureMode || (file_uuid ? "pdf" : "acknowledgement");
  const isAcknowledgement = signatureMode === "acknowledgement";
  const isAckConfigured = Boolean(acknowledgementText);
  // fieldLocations is an array prop, which BlockNote wipes on drag and isn't
  // re-hydrated server-side — so we mirror it into a JSON string and read from
  // whichever survived (the array normally; the parsed mirror after a drag).
  const fieldLocations = React.useMemo(() => {
    const arr = block.props.fieldLocations;
    if (Array.isArray(arr) && arr.length > 0) return arr;
    try {
      const parsed = JSON.parse(block.props.fieldLocationsJson || "null");
      return Array.isArray(parsed) ? parsed : arr || [];
    } catch (e) {
      return arr || [];
    }
  }, [block.props.fieldLocations, block.props.fieldLocationsJson]);

  // State for fresh presigned URL
  const [pdfUrl, setPdfUrl] = useState(null);
  const [completedPdfUrl, setCompletedPdfUrl] = useState(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState(null);
  // Drawn applied-signature image for the acknowledgement card, stored WITH its
  // source uuid: { uuid, url }. The card renders it only when the uuid matches
  // the current completion's applied_signature.image_uuid, so a stale URL from a
  // previous completion (or a failed/racing refetch) is never shown.
  const [signatureImage, setSignatureImage] = useState(null);

  // State for completion data from backend
  const [completionData, setCompletionData] = useState(null);
  const [isLoadingCompletion, setIsLoadingCompletion] = useState(false);

  // Check if document is already completed
  const isCompleted = Boolean(completionData?.is_completed);

  // Fetch fresh presigned URL when component mounts
  useEffect(() => {
    const fetchFreshUrl = async () => {
      if (file_uuid) {
        setIsLoadingUrl(true);
        setUrlError(null);
        try {
          const resFile = await getRightworkAPI(API_get_file_upload, {
            file_upload_uuid: file_uuid,
          });
          const freshUrl = resFile?.data?.redux?.payload?.presigned_url;
          if (freshUrl) {
            setPdfUrl(freshUrl);
          } else {
            setUrlError('Could not load PDF URL');
          }
        } catch (error) {
          console.error('Error fetching fresh PDF URL:', error);
          setUrlError('Failed to load document. Please try again.');
        } finally {
          setIsLoadingUrl(false);
        }
      }
    };

    fetchFreshUrl();
  }, [file_uuid]);

  // Use completion status from block props (now included in section data)
  useEffect(() => {
    const loadCompletionStatus = async () => {
      const blockData = block.props.signatureBlockData;

      // Skip in preview: blockData.user_has_completed is the viewing admin's
      // own completion (per user+section), which must not surface as a
      // "completed/signed" state in the track/module preview. Matches
      // DocumentUpload / ManagerInput. Only the live learner view loads it.
      if (props.mode === "complete" && !props.isPreview && blockData) {
        setIsLoadingCompletion(true);
        try {
          // Completion status is now included in the block data from the backend
          const isCompleted = blockData.user_has_completed === true;

          setCompletionData({
            is_completed: isCompleted,
            signature_completion: isCompleted ? {
              completed_pdf_uuid: blockData.completed_pdf_uuid,
              created_at: blockData.completed_at,
              signer_printed_name: blockData.signer_printed_name,
              applied_signature: blockData.applied_signature,
            } : null,
          });

          // If a drawn signature was applied, fetch its image for the card.
          // Clear any prior image first so a different completion's signature
          // never lingers while (or if) this fetch fails.
          const applied = isCompleted ? blockData.applied_signature : null;
          setSignatureImage(null);
          if (applied?.method === "drawn" && applied?.image_uuid) {
            try {
              const resSig = await getRightworkAPI(API_get_file_upload, {
                file_upload_uuid: applied.image_uuid,
              });
              const sigUrl = resSig?.data?.redux?.payload?.presigned_url;
              if (sigUrl) setSignatureImage({ uuid: applied.image_uuid, url: sigUrl });
            } catch (error) {
              console.warn('Signature image no longer available:', error);
            }
          }

          // If completed, fetch the completed PDF URL (if file still exists)
          if (isCompleted && blockData.completed_pdf_uuid) {
            try {
              const resFile = await getRightworkAPI(API_get_file_upload, {
                file_upload_uuid: blockData.completed_pdf_uuid,
              });
              const freshUrl = resFile?.data?.redux?.payload?.presigned_url;
              if (freshUrl) {
                setCompletedPdfUrl(freshUrl);
              }
            } catch (error) {
              // File no longer exists - completion record exists but file is gone
              console.warn('Completed PDF file no longer available:', error);
            }
          }
        } catch (error) {
          console.error('Error loading signature completion status:', error);
        } finally {
          setIsLoadingCompletion(false);
        }
      }
    };

    loadCompletionStatus();
  }, [props.mode, props.isPreview, block.props.signatureBlockData, props.sectionUUID]);

  const [openSignatureModal, setOpenSignatureModal] = useState(false);
  const handleOpenSignatureModal = () => setOpenSignatureModal(true);
  const handleCloseSignatureModal = () => setOpenSignatureModal(false);

  const [openReviewModal, setOpenReviewModal] = useState(false);
  const handleOpenReviewModal = () => setOpenReviewModal(true);
  const handleCloseReviewModal = () => setOpenReviewModal(false);

  // Acknowledgement-mode modals
  const [openAckConfigModal, setOpenAckConfigModal] = useState(false);
  const [openAckSignModal, setOpenAckSignModal] = useState(false);

  const handleSignatureDataSave = (signatureData) => {
    editor.updateBlock(block, {
      ...block,
      props: {
        ...block.props,
        ...signatureData,
        // Drag-proof JSON mirror of the field positions (the array prop is
        // wiped on drag). Kept in sync with the array on every save.
        ...(signatureData.fieldLocations !== undefined && {
          fieldLocationsJson: JSON.stringify(signatureData.fieldLocations || []),
        }),
      },
    });
  };

  // Handle completion of signature by employee. uploadedFileData carries the
  // signed PDF uuid plus (for the acknowledgement flow) the audit fields.
  const handleSignatureCompletion = async (uploadedFileData) => {
    const signatureBlockUUID = block.props.signatureBlockData?.uuid;
    if (!signatureBlockUUID) {
      console.error('No signature block UUID found');
      toast.error('Configuration error. Please contact your administrator.');
      return;
    }

    const params = {
      signature_block_uuid: signatureBlockUUID,
      section_uuid: props.sectionUUID,
      completed_pdf_uuid: uploadedFileData.uuid,
      company_id: props.homeCompany,
    };

    // Forward whichever audit fields the signing modal provided. Each flow
    // (acknowledgement / PDF) supplies the subset it captures; absent keys
    // stay out of the payload so the backend keeps NULL ("not applicable")
    // rather than a coerced default.
    const AUDIT_KEYS = [
      "signer_printed_name",
      "signature_method",
      "user_signature_uuid",
      "consent_agreed",
      "consent_disclosure_version",
      "field_values",
    ];
    for (const key of AUDIT_KEYS) {
      if (uploadedFileData[key] !== undefined) {
        params[key] = uploadedFileData[key];
      }
    }

    // Only include store_team_member_id if defined (manager viewing employee's training)
    if (props.employeeID) {
      params.store_team_member_id = props.employeeID;
    }

    // The SAVE is the only fatal step: rethrow so the signing modal surfaces a
    // real failure instead of showing a false "recorded" success. (We don't
    // toast here too, or the user gets two stacked error toasts.)
    let payload;
    try {
      const response = await postRightworkAPI(API_post_learning_signature_completion, params);
      payload = response?.data?.redux?.payload;
    } catch (error) {
      console.error('Error saving signature completion:', error);
      throw error;
    }

    // The completion is now persisted. Everything below is best-effort: updating
    // local state and minting a viewing URL. A failure here must NOT report a
    // save failure — otherwise the user retries into "Signature already
    // completed". The completed PDF URL re-hydrates on the next section load.
    setCompletionData({
      is_completed: true,
      signature_completion: {
        ...(payload?.signature_completion || {}),
        // The POST response doesn't carry the applied-signature snapshot, so
        // echo what the signing modal supplied — otherwise the card shows no
        // signature until the next hydrated section load.
        applied_signature: uploadedFileData.applied_signature || null,
        signer_printed_name: uploadedFileData.signer_printed_name,
      },
    });
    // Show a drawn signature image right away (the hydrated load re-fetches it).
    const appliedImageUuid = uploadedFileData.applied_signature?.image_uuid;
    if (uploadedFileData.applied_signature_image_url && appliedImageUuid) {
      setSignatureImage({
        uuid: appliedImageUuid,
        url: uploadedFileData.applied_signature_image_url,
      });
    } else {
      setSignatureImage(null);
    }

    try {
      const resFile = await getRightworkAPI(API_get_file_upload, {
        file_upload_uuid: uploadedFileData.uuid,
      });
      const freshUrl = resFile?.data?.redux?.payload?.presigned_url;
      if (freshUrl) {
        setCompletedPdfUrl(freshUrl);
      }
    } catch (error) {
      console.warn('Could not fetch completed PDF URL (non-fatal):', error);
    }
  };

  // Handle viewing completed document
  const handleViewCompleted = () => {
    if (completedPdfUrl) {
      window.open(completedPdfUrl, '_blank');
    } else if (isCompleted) {
      // Completion exists but file is missing
      toast.error('The completed document is no longer available.');
    }
  };

  // Count signature and text fields
  const signatureCount = fieldLocations?.filter(f => f.type === 'signature')?.length || 0;
  const textCount = fieldLocations?.filter(f => f.type === 'text')?.length || 0;

  // Complete/Preview mode - Shows the actual signing interface
  if (props.mode === "complete") {
    // Acknowledgement mode: read statement + sign (no PDF). Renders a native
    // card and the AcknowledgementSignModal.
    if (isAcknowledgement) {
      // An unconfigured acknowledgement (no statement) has nothing to sign.
      // Show an inert "not configured" card rather than a signable surface;
      // the backend likewise excludes it from completion gating.
      if (!isAckConfigured) {
        return (
          <div
            className="w-full rounded-lg px-4 py-4 border-2 border-dashed border-slate-200 bg-slate-50"
            contentEditable={false}
          >
            <div className="flex items-center gap-2">
              <DrawOutlinedIcon style={{ fontSize: 18 }} className="text-slate-400" />
              <p className="text-slate-500 text-sm">
                This acknowledgement has not been set up yet.
              </p>
            </div>
          </div>
        );
      }

      const handleAckCardClick = () => {
        if (isLoadingCompletion) return;
        if (isCompleted) {
          handleViewCompleted();
        } else {
          setOpenAckSignModal(true);
        }
      };

      return (
        <>
          <div
            className={`w-full rounded-lg px-4 py-4 border transition-all cursor-pointer ${
              isCompleted
                ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
            onClick={handleAckCardClick}
            contentEditable={false}
          >
            {isLoadingCompletion ? (
              <div className="py-6 flex items-center justify-center">
                <LoadSpinner size={24} />
              </div>
            ) : (
              <div className="flex items-start gap-3">
                {isCompleted ? (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                    <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 20 }} />
                  </div>
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                    <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base font-semibold text-gray-900">{title || 'Acknowledgement'}</h2>
                  {isCompleted ? (
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                      Signed
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                      Required
                    </span>
                  )}
                </div>
                <p className="text-slate-600 mt-1 line-clamp-2" style={{ fontSize: 13 }}>
                  {acknowledgementText}
                </p>
                {isCompleted ? (
                  <>
                    {/* The applied signature: drawn shows the image, typed
                        renders in the adopted font. */}
                    {(() => {
                      const applied = completionData?.signature_completion?.applied_signature;
                      const printedName = completionData?.signature_completion?.signer_printed_name;
                      if (!applied) return null;
                      return (
                        <div className="mt-2 inline-flex flex-col">
                          <div
                            className="px-3 bg-white border border-emerald-200 rounded-md inline-flex items-center"
                            style={{ minHeight: 44 }}
                          >
                            {applied.method === "drawn" ? (
                              (signatureImage && signatureImage.uuid === applied.image_uuid) ? (
                                <img
                                  src={signatureImage.url}
                                  alt="Signature"
                                  style={{ maxHeight: 32, maxWidth: 220 }}
                                />
                              ) : (
                                <span className="text-slate-400" style={{ fontSize: 12 }}>
                                  Signature on file
                                </span>
                              )
                            ) : (
                              <span
                                style={{
                                  fontFamily: fontFamilyForKey(applied.typed_font),
                                  fontSize: 24,
                                  color: "#0f172a",
                                  lineHeight: 1,
                                }}
                              >
                                {applied.typed_name || printedName}
                              </span>
                            )}
                          </div>
                          {printedName && (
                            <span className="text-slate-500 mt-1" style={{ fontSize: 11 }}>
                              {printedName}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    <p className="text-emerald-600 mt-2 font-medium" style={{ fontSize: 12 }}>
                      Signed on {new Date(completionData?.signature_completion?.created_at).toLocaleDateString()}
                    </p>
                  </>
                ) : (
                  <p className="text-slate-500 mt-2" style={{ fontSize: 12 }}>
                    Click anywhere in this card to read and sign
                  </p>
                )}
                </div>
              </div>
            )}
          </div>
          {/* Rendered unconditionally (not gated on isCompleted) so the modal's
              own success screen can show and self-close after submit, rather
              than being unmounted the instant the parent flips to completed.
              It only opens via openAckSignModal, which is false once completed. */}
          <AcknowledgementSignModal
            open={openAckSignModal}
            handleClose={() => setOpenAckSignModal(false)}
            title={title}
            statement={acknowledgementText}
            homeCompany={props.homeCompany}
            onComplete={handleSignatureCompletion}
            isPreview={props.isPreview}
          />
        </>
      );
    }

    // Handle click on the whole card
    const handleCardClick = () => {
      if (isLoadingUrl || isLoadingCompletion || urlError) return;

      if (isCompleted) {
        handleViewCompleted();
      } else if (pdfUrl) {
        handleOpenReviewModal();
      }
    };

    return (
      <>
        <div
          className={`w-full rounded-lg px-4 py-4 border transition-all ${
            isCompleted
              ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 cursor-pointer'
              : 'border-slate-200 bg-white cursor-pointer hover:border-slate-300'
          } ${isLoadingUrl || isLoadingCompletion || urlError ? '' : ''}`}
          onClick={handleCardClick}
          contentEditable={false}
        >
          {isLoadingUrl || isLoadingCompletion ? (
            <div className="py-6 flex items-center justify-center">
              <LoadSpinner size={24} />
            </div>
          ) : (
            <div className="flex items-start gap-3">
              {isCompleted ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                  <CheckCircleIcon className="text-emerald-600" style={{ fontSize: 20 }} />
                </div>
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                  <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-semibold text-gray-900">{pdfName || 'Document'}</h2>
                {isCompleted ? (
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                    Completed
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                    Required
                  </span>
                )}
              </div>

              {urlError ? (
                <p className="text-rose-600 mt-2" style={{ fontSize: 12 }}>
                  {urlError}
                </p>
              ) : isCompleted ? (
                <>
                  <p className="text-emerald-600 mt-1 font-medium" style={{ fontSize: 12 }}>
                    Completed on {new Date(completionData?.signature_completion?.created_at).toLocaleDateString()}
                  </p>
                  <p className="text-slate-500 mt-1" style={{ fontSize: 12 }}>
                    Click anywhere in this card to view completed document
                  </p>
                </>
              ) : (
                <p className="text-slate-500 mt-2" style={{ fontSize: 12 }}>
                  Click anywhere in this card to review and sign
                </p>
              )}

              <div className={`mt-3 pt-3 border-t ${isCompleted ? "border-emerald-100" : "border-gray-100"}`}>
                <div className="flex items-center gap-x-2">
                  <div>
                    <DrawOutlinedIcon style={{ fontSize: 18 }} />
                  </div>
                  <p className="text-slate-900 text-sm" style={{ fontSize: 14 }}>
                    {signatureCount} signature {signatureCount === 1 ? 'field' : 'fields'}
                  </p>
                </div>
                <div className="flex items-center gap-x-2 mt-1.5">
                  <div>
                    <DescriptionOutlinedIcon style={{ fontSize: 18 }} />
                  </div>
                  <p className="text-slate-900 text-sm" style={{ fontSize: 14 }}>
                    {textCount} text {textCount === 1 ? 'field' : 'fields'}
                  </p>
                </div>
              </div>
              </div>
            </div>
          )}
        </div>
        {!isCompleted && (
          <SignatureReviewModal
            open={openReviewModal}
            handleClose={handleCloseReviewModal}
            pdfUrl={pdfUrl}
            pdfName={pdfName}
            fieldLocations={fieldLocations}
            totalPages={totalPages}
            homeCompany={props.homeCompany}
            onComplete={handleSignatureCompletion}
            isPreview={props.isPreview}
          />
        )}
      </>
    );
  }

  // Edit mode - Shows the configuration interface
  const isConfigured = isAcknowledgement ? isAckConfigured : Boolean(file_uuid);

  // Switch the block into PDF mode then open the PDF authoring modal.
  const handleChoosePdf = () => {
    handleSignatureDataSave({ signatureMode: "pdf" });
    handleOpenSignatureModal();
  };

  return (
    <>
      {/* See Quiz.js — content: "inline" requires a DOM target for BlockNote's
          inline content area; we park it off-flow so it doesn't affect layout. */}
      <span
        ref={props.contentRef}
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
      />

      {!isConfigured ? (
        // Empty state - pick what kind of signature to collect
        <div className="my-3 w-full" contentEditable={false}>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setOpenAckConfigModal(true)}
              className="p-4 border-2 border-dashed border-violet-300 rounded-xl text-violet-700 hover:bg-violet-50 hover:border-violet-500 transition-colors flex flex-col items-center justify-center gap-1.5 text-center"
            >
              <DrawOutlinedIcon style={{ fontSize: 22 }} />
              <span className="text-sm font-medium">Acknowledgement</span>
              <span className="text-xs text-violet-500">Employees read a statement and sign</span>
            </button>
            <button
              onClick={handleChoosePdf}
              className="p-4 border-2 border-dashed border-slate-300 rounded-xl text-slate-700 hover:bg-slate-50 hover:border-slate-500 transition-colors flex flex-col items-center justify-center gap-1.5 text-center"
            >
              <DescriptionOutlinedIcon style={{ fontSize: 22 }} />
              <span className="text-sm font-medium">PDF document</span>
              <span className="text-xs text-slate-500">Upload a PDF for employees to sign</span>
            </button>
          </div>
        </div>
      ) : isAcknowledgement ? (
        // Acknowledgement configured - show summary. Uses plain divs with
        // explicit inline typography (not h2/p) so BlockNote's prose/heading
        // CSS in the editor can't override the card's spacing or font sizes.
        <div
          className="bg-white rounded-xl relative w-full border border-slate-200 px-5 py-4"
          contentEditable={false}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                <DrawOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-slate-900 font-semibold truncate"
                  style={{ fontSize: 16, lineHeight: 1.4, margin: 0 }}
                >
                  {title || "Acknowledgement"}
                </div>
                <div
                  className="text-slate-600 line-clamp-2"
                  style={{ fontSize: 14, lineHeight: 1.5, marginTop: 4 }}
                >
                  {acknowledgementText}
                </div>
                <div
                  className="text-slate-400"
                  style={{ fontSize: 12, lineHeight: 1.4, marginTop: 8 }}
                >
                  Employees read this statement and sign to acknowledge it
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpenAckConfigModal(true)}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium shrink-0 transition-colors"
            >
              <EditIcon style={{ fontSize: 18 }} />
              Edit
            </button>
          </div>
        </div>
      ) : (
        // Manager view - PDF configured, show summary. Same treatment as the
        // acknowledgement card: violet icon box + divs with inline typography
        // so BlockNote's prose CSS can't override spacing or font sizes.
        <div
          className="bg-white rounded-xl relative w-full border border-slate-200 px-5 py-4"
          contentEditable={false}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                <DescriptionOutlinedIcon className="text-violet-600" style={{ fontSize: 20 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className="text-slate-900 font-semibold truncate"
                  style={{ fontSize: 16, lineHeight: 1.4, margin: 0 }}
                >
                  {pdfName || "Signature Document"}
                </div>
                <div
                  className="text-slate-500"
                  style={{ fontSize: 14, lineHeight: 1.5, marginTop: 4 }}
                >
                  PDF signature block configured for employee completion
                </div>
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-violet-500 rounded-full shrink-0"></div>
                    <span className="text-slate-700" style={{ fontSize: 13 }}>
                      {totalPages || 1} {totalPages === 1 ? "page" : "pages"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-violet-500 rounded-full shrink-0"></div>
                    <span className="text-slate-700" style={{ fontSize: 13 }}>
                      {signatureCount} signature {signatureCount === 1 ? "field" : "fields"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-violet-500 rounded-full shrink-0"></div>
                    <span className="text-slate-700" style={{ fontSize: 13 }}>
                      {textCount} text {textCount === 1 ? "field" : "fields"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handleOpenSignatureModal}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium shrink-0 transition-colors"
            >
              <EditIcon style={{ fontSize: 18 }} />
              Edit
            </button>
          </div>
        </div>
      )}
      <SignatureModal
        open={openSignatureModal}
        handleClose={handleCloseSignatureModal}
        existingData={{
          pdfName,
          file_uuid: file_uuid,
          fieldLocations,
          totalPages,
          saveToProfile,
        }}
        handleSignatureDataSave={handleSignatureDataSave}
        homeCompany={props.homeCompany}
      />
      <AcknowledgementConfigModal
        open={openAckConfigModal}
        handleClose={() => setOpenAckConfigModal(false)}
        existingData={{ title, acknowledgementText, saveToProfile }}
        handleSignatureDataSave={handleSignatureDataSave}
      />
    </>
  );
};
